import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { IconButton, InputAdornment } from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

import { OrganizationDropdownActions } from './organization-dropdown-actions';
import { loadOrgs } from '../action/load-orgs';
import { orgSelector, orgsForAccountId } from '../reducers/org-selectors';

import { ProductTelemetryController } from '../product-telemetry/product-telemetry-controller';
import { UiFilterTextField } from '../ui/ui-filter-text-field';
import { orgRoute } from '../web-routes';

import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';
import { SORT_ORDER, sortAlphabetically } from '../util/sort-functions';
import { OrgDropdownOption } from '../widgets/org-dropdown-option';

const mapStateToProps = (state: any, { accountId, orgId }: InputProps) => ({
  selectedOrg: orgSelector(state, orgId),
  activeOrgs: orgsForAccountId(state, accountId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { accountId }: InputProps,
) => ({
  actions: {
    loadOrgs: () => dispatch(loadOrgs(accountId)),
  },
});

type InputProps = {
  orgId: number;
  accountId: number;
  isOnMobile?: boolean;
  displayInline?: boolean;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps & RouteComponentProps;

const StyledExpandMoreIcon = () => (
  <MaterialDesignIcon iconName={ICON_NAMES.EXPAND_MORE} className="u-margin-right-lg" />
);

const PlaceHolderOption = () => (
  <div className="navDropdown">
    <div className="navDropdown-nameContainer">
      <div className="navDropdown-name u-margin">Please Select an Organization</div>
      <StyledExpandMoreIcon />
    </div>
  </div>
);

export const OrganizationDropdown = withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )((props: Props) => {
    const { actions, selectedOrg, activeOrgs, isOnMobile, history, displayInline } = props;
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const width = isOnMobile ? 'u-width-100' : 'u-width-300px';
    const textFieldWidth = isOnMobile ? 'u-width-100' : 'u-width-275px';
    const menuMaxHeight = menuOpen ? 450 : 0;
    const orgMenuMaxHeight = menuOpen ? 200 : 0;

    useEffect(() => {
      actions.loadOrgs();
    });

    if (!activeOrgs || !selectedOrg) {
      return null;
    }

    if (!selectedOrg.isActive && !activeOrgs.length && displayInline) {
      return null;
    }

    const toggleMenu = () => setMenuOpen(!menuOpen);
    const handleCloseMenu = () => {
      setSearchTerm('');
      setMenuOpen(false);
    };
    const handleOrgClick = (newOrgId: number) => () => {
      setSearchTerm('');

      if (selectedOrg.org_id === newOrgId) {
        setMenuOpen(false);
        return;
      }

      ProductTelemetryController.logEvent('org selector clicked', {
        value: newOrgId,
        metadata: {
          numOrgs: String(activeOrgs?.length),
          selectorVersion: 'new',
        },
      });

      history.replace(orgRoute(newOrgId));
      setMenuOpen(false);
    };

    const handleChangeSearchTerm = (newSearchTerm: string) => {
      setSearchTerm(newSearchTerm || '');
    };

    const FirstOrgOption = () => {
      if (!selectedOrg.isActive) {
        // If we are here, we are trying to route the user away from an inactive org
        return <PlaceHolderOption />;
      }
      if (!activeOrgs.length) {
        // If we are here, that means the only active org is the selected org
        return <OrgDropdownOption org={selectedOrg} isSelected={false} isTitleOption />;
      }

      return (
        <OrgDropdownOption
          org={selectedOrg}
          Icon={<StyledExpandMoreIcon />}
          isSelected={false}
          isTitleOption
        />
      );
    };

    const orgsToDisplay = activeOrgs
      .filter((org) => org.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort(sortAlphabetically((o) => o.name, SORT_ORDER.ASC));

    const showOrgOptions =
      (!selectedOrg.isActive && activeOrgs.length >= 1) || activeOrgs.length > 1;

    return (
      <ClickAwayListener onClickAway={handleCloseMenu}>
        <div>
          <div
            className={`u-cursor-pointer ${
              displayInline ? 'u-border' : 'u-hover-highlight'
            } ${width}`}
            role="presentation"
            onClick={toggleMenu}
          >
            {isOnMobile ? (
              <IconButton
                color="primary"
                className="u-backgroundHover-transparent u-cursor-pointer u-spread u-padding-lg"
                size="small"
                onClick={toggleMenu}
              >
                <MaterialDesignIcon
                  iconName={ICON_NAMES.MENU}
                  color="white"
                  className="u-margin-sm"
                />
              </IconButton>
            ) : (
              <FirstOrgOption />
            )}
          </div>
          {menuOpen ? (
            <div
              className={`u-border u-background-white u-color-black u-position-absolute ${width}`}
              style={{ maxHeight: menuMaxHeight, ...(isOnMobile ? { right: 0 } : {}) }}
            >
              <div className="u-elevation-3">
                {showOrgOptions ? (
                  <>
                    <div className={`u-padding-y-md u-border-bottom ${width}`}>
                      <UiFilterTextField
                        className={`u-padding-sm u-margin-left ${textFieldWidth}`}
                        textBoxWidth={textFieldWidth}
                        placeholder="Search by Organization Name"
                        label=""
                        value={searchTerm}
                        onChange={handleChangeSearchTerm}
                        startAdornment={
                          <InputAdornment position="start">
                            <MaterialDesignIcon iconName={ICON_NAMES.SEARCH} size="sm" />
                          </InputAdornment>
                        }
                      />
                    </div>
                    <div
                      className="u-overflowY-scroll u-border-bottom"
                      style={{ maxHeight: orgMenuMaxHeight }}
                    >
                      {orgsToDisplay.map((org) => (
                        <div
                          key={org.org_id}
                          role="presentation"
                          onClick={handleOrgClick(org.org_id)}
                        >
                          <OrgDropdownOption
                            org={org}
                            isSelected={org.org_id === selectedOrg.org_id}
                            isSelectable
                          />
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}
                {displayInline ? null : <OrganizationDropdownActions onClose={handleCloseMenu} />}
              </div>
            </div>
          ) : null}
        </div>
      </ClickAwayListener>
    );
  }),
);
